// src/waiter/WaiterDashboard.js
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

import logo from "../admin/logo.png";
import TablesSection from "./TablesSection";
import OrdersBarSection from "./OrdersReadySection";

/* ====================================================================== */
export default function WaiterDashboard() {
  const navigate = useNavigate();

  /* state */
  const [me, setMe]             = useState(null);
  const [filter, setFilter]     = useState("");
  const [mineOnly, setMineOnly] = useState(
    () => JSON.parse(localStorage.getItem("waiterMineOnly") || "false")
  );
  const [tables, setTables]     = useState([]);
  const [orders, setOrders]     = useState([]);
  const [currentView, setView]  = useState("tables");
  const [readyBadge, setReady]  = useState(0);

  /* api helpers -------------------------------------------------------- */
  const fetchTables = useCallback(async () => {
    if (!me) return;
    try {
      const params = mineOnly && me.role === "waiter" ? { mine: true } : {};
      const { data } = await api.get("/tables", { params });
      setTables(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("fetchTables:", e);
    }
  }, [me, mineOnly]);

  const fetchOrders = useCallback(async () => {
    if (!me) return;
    try {
      const { data } = await api.get("/orders");
      setOrders(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("fetchOrders:", e);
    }
  }, [me]);

  /* bootstrap ---------------------------------------------------------- */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users/me");
        setMe(data);
      } catch (e) {
        console.error("fetchMe:", e);
      }
    })();
  }, []);

  /* fetch on me / mineOnly change */
  useEffect(() => {
    if (me) {
      fetchTables();
      fetchOrders();
    }
    localStorage.setItem("waiterMineOnly", JSON.stringify(mineOnly));
  }, [me, mineOnly, fetchTables, fetchOrders]);

  /* websocket ---------------------------------------------------------- */
  useEffect(() => {
    const ws = new WebSocket(`${process.env.REACT_APP_WS_URL}/ws`);
    ws.onmessage = (e) => {
      if (["reload_tables", "reload_orders", "reload_kitchen"].includes(e.data)) {
        fetchTables();
        fetchOrders();
      }
    };
    return () => ws.close();
  }, [fetchTables, fetchOrders]);

  /* badge “pronti” ----------------------------------------------------- */
  useEffect(() => {
    if (!me) return;
    const vis =
      mineOnly && me.role === "waiter"
        ? orders.filter((o) => o.waiter_username === me.username)
        : orders;
    const ready = vis.filter(
      (o) => o.items?.length && o.items.every((it) => it.status === "ready")
    );
    setReady(ready.length);
  }, [orders, mineOnly, me]);

  /* handlers ----------------------------------------------------------- */
  const openSession = async (tableId) => {
    try {
      await api.post("/table_sessions", { table_id: tableId, status: "active" });
      fetchTables();
    } catch (e) { console.error(e); }
  };
  const enterTable = (t) => {
    if (!t.sessions_active_id) { alert("Nessuna sessione attiva"); return; }
    navigate(`/pos/${t.id}/${t.sessions_active_id}`);
  };
  const closeSession = async (tableId) => {
    try {
      const { data } = await api.get("/table_sessions");
      const active = data.find((s) => s.table_id === tableId && s.status === "active");
      if (!active) return alert("Nessuna sessione attiva");
      await api.patch(`/table_sessions/${active.id}`, { status: "closed" });
      fetchTables();
    } catch (e) { console.error(e); }
  };
  const takeOver = async (sessionId, userId) => {
    try {
      await api.patch(`/table_sessions/${sessionId}/assign_waiter`, { waiter_id: userId });
      fetchTables();
    } catch (e) {
      console.error(e);
      alert("Impossibile prendere in carico il tavolo");
    }
  };

  /* filtering */
  const filtered = tables.filter((t) =>
    String(t.table_number).includes(filter)
  );

  /* render ------------------------------------------------------------- */
  return (
    <div style={styles.container}>
      {/* header */}
      <header style={styles.header}>
        <div
          style={styles.logoArea}
          role="button"
          tabIndex={0}
          onClick={() => window.location.reload()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") window.location.reload();
          }}
        >
          <img src={logo} alt="RistoBuddy" style={styles.logoImage} />
          <span style={styles.logoText}>RistoBuddy</span>
        </div>

        {me?.role === "waiter" && (
          <label style={styles.mineToggle}>
            <input
              type="checkbox"
              checked={mineOnly}
              onChange={() => setMineOnly((p) => !p)}
            />
            Solo i miei
          </label>
        )}
      </header>

      {/* main */}
      {currentView === "tables" && (
        <div style={styles.sectionVisible}>
          <TablesSection
            tables={filtered}
            filter={filter}
            setFilter={setFilter}
            myUserId={me?.id}
            onOpenSession={openSession}
            onCloseSession={closeSession}
            onEnterTable={enterTable}
            onTakeOverTable={takeOver}
            reloadTables={fetchTables}   // <-- senza commenti
          />
        </div>
      )}

      {currentView === "orders" && (
        <div style={styles.sectionVisible}>
          <OrdersBarSection
            orders={orders}
            refreshOrders={fetchOrders}
            myWaiterName={mineOnly && me?.role === "waiter" ? me.username : null}
          />
        </div>
      )}

      {/* footer */}
      <footer style={styles.bottomNav}>
        <NavItem
          label="Tavoli"
          icon="🍽"
          active={currentView === "tables"}
          onClick={() => setView("tables")}
        />
        <NavItem
          label="Ordini"
          icon="🔔"
          badge={readyBadge}
          active={currentView === "orders"}
          onClick={() => setView("orders")}
        />
      </footer>
    </div>
  );
}

/* footer nav item */
function NavItem({ label, icon, badge = 0, active, onClick }) {
  return (
    <div
      style={active ? styles.navItemActive : styles.navItem}
      onClick={onClick}
    >
      <span style={styles.navIcon}>{icon}</span>
      {badge > 0 && <span style={styles.ordersBadge}>{badge}</span>}
      <span style={styles.navLabel}>{label}</span>
    </div>
  );
}

/* styles (immutati) */
const palette = {
  primaryStart: "#FF8C00",
  primaryEnd: "#FF6600",
  textLight: "#FFFFFF",
  textDark: "#000000",
  bgLight: "#FAFAFA",
};

const styles = {
  container: {
    position: "relative",
    minHeight: "100vh",
    backgroundColor: palette.bgLight,
    display: "flex",
    flexDirection: "column",
    paddingBottom: 70,
    fontSize: 16,
  },
  header: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    background: `linear-gradient(90deg, ${palette.primaryStart}, ${palette.primaryEnd})`,
    boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
    padding: "10px 16px",
  },
  logoArea: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
  },
  logoImage: { height: 34, marginRight: 10 },
  logoText: { color: palette.textDark, fontWeight: "bold", fontSize: 18 },
  mineToggle: {
    marginLeft: "auto",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 14,
  },
  sectionVisible: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: "10px 16px",
  },
  bottomNav: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    height: 70,
    background: `linear-gradient(180deg, ${palette.primaryStart}, ${palette.primaryEnd})`,
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    color: palette.textLight,
    boxShadow: "0 -2px 5px rgba(0,0,0,0.2)",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    zIndex: 999,
  },
  navItem: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: 60,
    textAlign: "center",
    color: palette.textLight,
    fontSize: 13,
    cursor: "pointer",
  },
  navItemActive: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: 60,
    textAlign: "center",
    color: palette.textLight,
    fontSize: 13,
    fontWeight: "bold",
    cursor: "pointer",
  },
  navIcon: { fontSize: 22, lineHeight: 1.2, marginBottom: 4 },
  navLabel: { fontSize: 12, lineHeight: "14px" },
  ordersBadge: {
    position: "absolute",
    top: -4,
    right: 14,
    background: "red",
    color: "#fff",
    borderRadius: "50%",
    padding: "2px 6px",
    fontSize: 11,
  },
};
